import {lazy, Suspense} from "react";
import {Routes, Route, useLocation} from "react-router-dom";
import {AnimatePresence, motion} from "framer-motion";
import {CartProvider} from "./contexts/CartContext";
import {AuthProvider} from "./contexts/AuthContext";
import {SiteOptionsProvider} from "./contexts/SiteOptionsContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Projects from "./pages/Projects";
import Shop from "./pages/Shop";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import ProtectedRoute from "./components/ProtectedRoute";

// Admin routes are code-split: the admin bundle (react-quill, admin
// components, etc.) only loads when someone actually visits /admin*.
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));

const AdminFallback = () => (
  <div className="flex items-center justify-center py-24">
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
      role="status"
      aria-label="Loading admin"
    />
  </div>
);

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{opacity: 0, y: 6}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0, y: -6}}
        transition={{duration: 0.18, ease: "easeOut"}}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<AdminFallback />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Suspense fallback={<AdminFallback />}>
                  <Admin />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <AuthProvider>
      <SiteOptionsProvider>
        <CartProvider>
          <Layout>
            <AnimatedRoutes />
          </Layout>
        </CartProvider>
      </SiteOptionsProvider>
    </AuthProvider>
  );
}

export default App;
